// pages/Home.jsx - Updated Core Operations Section with Background Images
import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  alpha,
  LinearProgress,
  Stack,
  Divider,
  Zoom,
  Fade,
} from '@mui/material';
import { keyframes } from '@mui/system';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VerifiedIcon from '@mui/icons-material/Verified';
import SecurityIcon from '@mui/icons-material/Security';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TimelineIcon from '@mui/icons-material/Timeline';
import PublicIcon from '@mui/icons-material/Public';
import GroupsIcon from '@mui/icons-material/Groups';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import FactoryIcon from '@mui/icons-material/Factory';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DiamondIcon from '@mui/icons-material/Diamond';
import GppGoodIcon from '@mui/icons-material/GppGood';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import HeroSection from '../components/Sections/HeroSection';
import LivePricesSection from '../components/Sections/LivePricesSection';
import CTASection from '../components/Sections/CTASection';
import { motion } from 'framer-motion';

// Ultra-premium animations
const goldShimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const floatSlow = keyframes`
  0% {
    transform: translate(0px, 0px) rotate(0deg);
  }
  33% {
    transform: translate(20px, -15px) rotate(2deg);
  }
  66% {
    transform: translate(-10px, 10px) rotate(-1deg);
  }
  100% {
    transform: translate(0px, 0px) rotate(0deg);
  }
`;

const floatMedium = keyframes`
  0% {
    transform: translate(0px, 0px) rotate(0deg);
  }
  33% {
    transform: translate(-15px, -10px) rotate(-2deg);
  }
  66% {
    transform: translate(15px, 8px) rotate(1deg);
  }
  100% {
    transform: translate(0px, 0px) rotate(0deg);
  }
`;

const floatFast = keyframes`
  0% {
    transform: translate(0px, 0px) scale(1);
  }
  50% {
    transform: translate(8px, -8px) scale(1.05);
  }
  100% {
    transform: translate(0px, 0px) scale(1);
  }
`;

const shimmerBackground = keyframes`
  0% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 0.3;
  }
`;

// Generate static particle positions
const generateParticlePositions = () => {
  return [...Array(20)].map((_, i) => ({
    id: i,
    top: Math.random() * 100,
    left: Math.random() * 100,
    width: 2 + Math.random() * 4,
    height: 2 + Math.random() * 4,
    opacity: 0.1 + Math.random() * 0.3,
    animationDuration: 15 + Math.random() * 20,
  }));
};

// Abstract Background Component
const AbstractBackground = () => {
  const particles = useMemo(() => generateParticlePositions(), []);
  
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 30%, #0a0a2a 0%, #0d0d35 50%, #060620 100%)',
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '-5%',
          width: '40%',
          height: '40%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0) 70%)',
          animation: `${floatSlow} 25s ease-in-out infinite`,
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          bottom: '5%',
          right: '-5%',
          width: '50%',
          height: '50%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0) 70%)',
          animation: `${floatMedium} 30s ease-in-out infinite`,
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          top: '30%',
          right: '10%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(0,100,255,0.08) 0%, rgba(0,100,255,0) 70%)',
          borderRadius: '50%',
          animation: `${floatFast} 20s ease-in-out infinite`,
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          left: '5%',
          width: '250px',
          height: '250px',
          background: 'radial-gradient(circle, rgba(0,150,255,0.06) 0%, rgba(0,150,255,0) 70%)',
          borderRadius: '50%',
          animation: `${floatSlow} 22s ease-in-out infinite reverse`,
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          top: '15%',
          left: '20%',
          width: '200px',
          height: '2px',
          background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent)',
          transform: 'rotate(25deg)',
          animation: `${shimmerBackground} 4s ease-in-out infinite`,
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          bottom: '25%',
          right: '15%',
          width: '300px',
          height: '2px',
          background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.25), transparent)',
          transform: 'rotate(-15deg)',
          animation: `${shimmerBackground} 5s ease-in-out infinite 1s`,
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          top: '60%',
          left: '10%',
          width: '150px',
          height: '150px',
          border: '1px solid rgba(212,175,55,0.08)',
          borderRadius: '50%',
          animation: `${floatMedium} 18s ease-in-out infinite`,
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          right: '25%',
          width: '100px',
          height: '100px',
          border: '1px solid rgba(0,150,255,0.1)',
          borderRadius: '50%',
          animation: `${floatFast} 15s ease-in-out infinite reverse`,
        }}
      />
      
      {particles.map((particle) => (
        <Box
          key={`particle-${particle.id}`}
          sx={{
            position: 'absolute',
            top: `${particle.top}%`,
            left: `${particle.left}%`,
            width: `${particle.width}px`,
            height: `${particle.height}px`,
            background: `rgba(212, 175, 55, ${particle.opacity})`,
            borderRadius: '50%',
            animation: `${floatFast} ${particle.animationDuration}s ease-in-out infinite`,
            opacity: 0.6,
          }}
        />
      ))}
      
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'linear-gradient(rgba(212,175,55,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.02) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
};

// Ultra Premium Section Header
const UltraPremiumHeader = ({ title, subtitle, align = 'center' }) => (
  <Box sx={{ mb: 5, textAlign: align }}>
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        mb: 2,
        px: 2,
        py: 0.5,
        borderRadius: 4,
        background: alpha('#D4AF37', 0.12),
        border: '1px solid rgba(212, 175, 55, 0.25)',
        backdropFilter: 'blur(5px)',
      }}
    >
      <DiamondIcon sx={{ fontSize: 12, color: '#D4AF37' }} />
      <Typography
        sx={{
          color: '#D4AF37',
          fontWeight: 600,
          letterSpacing: 2,
          fontSize: '0.65rem',
          textTransform: 'uppercase',
        }}
      >
        {subtitle}
      </Typography>
      <DiamondIcon sx={{ fontSize: 12, color: '#D4AF37' }} />
    </Box>
    
    <Typography
      variant="h3"
      sx={{
        fontSize: { xs: 28, sm: 34, md: 40 },
        fontWeight: 800,
        background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 50%, #FFD700 100%)',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        mb: 1.5,
        position: 'relative',
        display: 'inline-block',
        letterSpacing: '-0.02em',
      }}
    >
      {title}
    </Typography>
    
    <Box
      sx={{
        width: 60,
        height: 3,
        background: 'linear-gradient(90deg, #D4AF37, #FFD700, #D4AF37)',
        margin: '0 auto',
        borderRadius: 2,
        mt: 2,
      }}
    />
  </Box>
);

// Main Home Component
const Home = () => {
  const [prices, setPrices] = useState({
    gold: null,
    silver: null,
    platinum: null,
    palladium: null,
  });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const mockPrices = {
          gold: { price: 2150.75, change: 12.30, changePercent: 0.58 },
          silver: { price: 24.85, change: 0.42, changePercent: 1.72 },
          platinum: { price: 912.30, change: -5.20, changePercent: -0.57 },
          palladium: { price: 987.50, change: 8.75, changePercent: 0.89 },
        };
        
        await new Promise(resolve => setTimeout(resolve, 500));
        
        setPrices(mockPrices);
        setLastUpdated(new Date());
        setLoading(false);
        setError(null);
      } catch (error) {
        console.error('Error fetching prices:', error);
        setError('Failed to load prices');
        setLoading(false);
      }
    };

    fetchPrices();
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, []);

  // Core Operations Cards with Background Images
  const coreOperationsCards = [
    {
      icon: <FactoryIcon sx={{ fontSize: 28, color: '#D4AF37' }} />,
      title: "Mining Operations",
      description: "State-of-the-art extraction facilities across three continents with sustainable practices.",
      chipLabel: "12+ ACTIVE MINES",
      statValue: "3",
      statLabel: "Continents",
      backgroundImage: "/bgimage/mining.jpg",
    },
    {
      icon: <WhatshotIcon sx={{ fontSize: 28, color: '#D4AF37' }} />,
      title: "Premium Refining",
      description: "Advanced metallurgical processes achieving 99.99% purity levels, LBMA certified.",
      chipLabel: "500K+ OZ MONTHLY",
      statValue: "99.99%",
      statLabel: "Purity",
      backgroundImage: "/bgimage/goldenprizebars.jpg",
    },
    {
      icon: <PrecisionManufacturingIcon sx={{ fontSize: 28, color: '#D4AF37' }} />,
      title: "Metal Processing",
      description: "Custom processing solutions for industrial applications from electronics to aerospace.",
      chipLabel: "25+ ALLOYS",
      statValue: "25+",
      statLabel: "Alloys",
      backgroundImage: "/bgimage/metalprocessing.jpg",
    },
    {
      icon: <LocalShippingIcon sx={{ fontSize: 28, color: '#D4AF37' }} />,
      title: "Global Distribution",
      description: "Secure logistics network with real-time tracking and guaranteed delivery windows worldwide.",
      chipLabel: "40+ COUNTRIES",
      statValue: "40+",
      statLabel: "Countries",
      backgroundImage: "/bgimage/destribution.jpg",
    },
  ];

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh', overflowX: 'hidden' }}>
      <AbstractBackground />
      
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <HeroSection />
        <LivePricesSection 
          prices={prices} 
          loading={loading} 
          lastUpdated={lastUpdated}
          error={error}
        />
        
        {/* SECTION 1: CORE OPERATIONS - 2 ROWS x 2 COLUMNS with BACKGROUND IMAGES */}
        <Box sx={{ py: { xs: 6, md: 8 }, position: 'relative' }}>
          <Container maxWidth="lg">
            {/* Section Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Chip
                label="CORE OPERATIONS"
                sx={{
                  backgroundColor: alpha('#D4AF37', 0.12),
                  color: '#D4AF37',
                  fontWeight: 700,
                  letterSpacing: 1,
                  mb: 2,
                  fontSize: '0.7rem',
                  px: 2,
                  height: 26,
                  borderRadius: '6px'
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 50%, #FFD700 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                  fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
                }}
              >
                Integrated Metals Operations
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: alpha('#FFFFFF', 0.7),
                  maxWidth: '600px',
                  mx: 'auto',
                  fontSize: '0.9rem'
                }}
              >
                End-to-end solutions from extraction to global distribution
              </Typography>
            </Box>

            {/* 2 ROWS x 2 COLUMNS Grid with Background Images */}
            <Grid container spacing={2.5}>
              {coreOperationsCards.map((card, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                    whileHover={{ y: -4 }}
                    style={{ height: '100%' }}
                  >
                    <Card
                      sx={{
                        bgcolor: '#FFFFFF',
                        borderRadius: 3,
                        overflow: 'hidden',
                        position: 'relative',
                        transition: 'all 0.3s ease',
                        border: '1px solid rgba(212, 175, 55, 0.15)',
                        height: '280px',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                        cursor: 'pointer',
                        backgroundImage: `url(${card.backgroundImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.6) 100%)',
                          zIndex: 0,
                        },
                        '&:hover': {
                          boxShadow: '0 8px 24px rgba(212, 175, 55, 0.12)',
                          borderColor: alpha('#D4AF37', 0.3),
                          '& .icon-box': {
                            background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                            '& svg': { color: '#FFFFFF' }
                          },
                          '&::before': {
                            background: 'linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.45) 100%)',
                          }
                        },
                      }}
                    >
                      {/* Decorative top bar */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background: 'linear-gradient(90deg, #D4AF37, #FFD700, #D4AF37)',
                          zIndex: 1,
                        }}
                      />
                      
                      <CardContent sx={{ 
                        p: 2.5, 
                        flex: 1, 
                        display: 'flex', 
                        flexDirection: 'column',
                        position: 'relative',
                        zIndex: 1,
                        color: '#FFFFFF',
                      }}>
                        {/* Header with Icon and Chip */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Box
                            className="icon-box"
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: '12px',
                              background: alpha('#D4AF37', 0.2),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.3s ease',
                              backdropFilter: 'blur(4px)',
                            }}
                          >
                            {card.icon}
                          </Box>
                          <Chip
                            label={card.chipLabel}
                            size="small"
                            sx={{
                              bgcolor: alpha('#000000', 0.6),
                              color: '#D4AF37',
                              fontSize: '0.55rem',
                              height: 20,
                              fontWeight: 600,
                              backdropFilter: 'blur(4px)',
                            }}
                          />
                        </Box>
                        
                        {/* Title */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#FFFFFF', mb: 0.75, fontSize: '1rem' }}>
                          {card.title}
                        </Typography>
                        
                        {/* Description - shortened for smaller card */}
                        <Typography sx={{ color: alpha('#FFFFFF', 0.85), lineHeight: 1.4, fontSize: '0.75rem', mb: 1.5, flex: 1 }}>
                          {card.description.length > 100 ? card.description.substring(0, 100) + '...' : card.description}
                        </Typography>
                        
                        <Divider sx={{ my: 1, borderColor: alpha('#D4AF37', 0.3) }} />
                        
                        {/* Stats Footer */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#D4AF37', fontSize: '1.1rem', lineHeight: 1 }}>
                              {card.statValue}
                            </Typography>
                            <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.7), fontSize: '0.6rem' }}>
                              {card.statLabel}
                            </Typography>
                          </Box>
                          <ArrowForwardIcon sx={{ fontSize: 14, color: '#D4AF37', transition: 'transform 0.3s ease' }} />
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            {/* View All Button */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="outlined"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  borderColor: '#D4AF37',
                  color: '#D4AF37',
                  px: 4,
                  py: 0.8,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: '#D4AF37',
                    bgcolor: alpha('#D4AF37', 0.05),
                    transform: 'translateX(5px)',
                  },
                }}
              >
                EXPLORE ALL OPERATIONS
              </Button>
            </Box>
          </Container>
        </Box>

        {/* Section 2: The Pentillion Difference - 2 Columns (REMOVED $2.5B+ and 500+) */}
        <Box sx={{ py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="lg">
            <UltraPremiumHeader 
              title="The Pentillion Difference" 
              subtitle="UNPARALLELED EXCELLENCE"
            />
            
            <Grid container spacing={5} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Fade in timeout={800}>
                  <Box sx={{ height: '100%' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 3,
                      }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <DiamondIcon sx={{ color: '#0a0a2a', fontSize: 24 }} />
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{
                          background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 100%)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          fontWeight: 800,
                          fontSize: { xs: 22, md: 28 },
                          letterSpacing: '-0.02em',
                        }}
                      >
                        Setting the Standard
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        color: alpha('#FFFFFF', 0.8),
                        mb: 4,
                        lineHeight: 1.7,
                        fontSize: '0.95rem',
                      }}
                    >
                      For over two decades, Pentillion has been at the forefront of the global metals industry, 
                      providing unparalleled quality, transparency, and reliability to Fortune 500 clients worldwide.
                    </Typography>
                    
                    <Stack spacing={2}>
                      {[
                        { text: "LBMA & LPPM Accredited Refineries", icon: <VerifiedIcon /> },
                        { text: "Real-time Market Access & Pricing", icon: <TimelineIcon /> },
                        { text: "Secure, Insured Global Logistics", icon: <SecurityIcon /> },
                        { text: "24/7 Dedicated Client Support", icon: <SupportAgentIcon /> },
                        { text: "Sustainable & Ethical Sourcing", icon: <PublicIcon /> },
                      ].map((item, idx) => (
                        <Zoom in timeout={600 + idx * 100} key={idx}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              p: 1.5,
                              borderRadius: 2,
                              transition: 'all 0.3s ease',
                              bgcolor: alpha('#FFFFFF', 0.05),
                              border: '1px solid rgba(212, 175, 55, 0.15)',
                              '&:hover': {
                                transform: 'translateX(8px)',
                                borderColor: alpha('#D4AF37', 0.4),
                                bgcolor: alpha('#FFFFFF', 0.08),
                              },
                            }}
                          >
                            <Box sx={{ color: '#D4AF37' }}>{item.icon}</Box>
                            <Typography sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.9rem' }}>
                              {item.text}
                            </Typography>
                          </Box>
                        </Zoom>
                      ))}
                    </Stack>

                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        mt: 4,
                        background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                        color: '#0a0a2a',
                        px: 4,
                        py: 1.2,
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        letterSpacing: '1px',
                        borderRadius: 2,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                          transform: 'translateX(5px)',
                        },
                      }}
                    >
                      DISCOVER OUR CAPABILITIES
                    </Button>
                  </Box>
                </Fade>
              </Grid>

              <Grid item xs={12} md={6}>
                <Fade in timeout={1000}>
                  <Grid container spacing={2.5} sx={{ height: '100%' }}>
                    {/* ONLY KEPT TWO STATS: 99.99% and 25+ */}
                    {[
                      { value: "99.99%", label: "Purity Achieved", icon: <WorkspacePremiumIcon sx={{ fontSize: 28 }} /> },
                      { value: "25+", label: "Years of Excellence", icon: <EmojiEventsIcon sx={{ fontSize: 28 }} /> },
                    ].map((stat, idx) => (
                      <Grid item xs={12} sm={6} key={idx}>
                        <Box
                          sx={{
                            bgcolor: '#FFFFFF',
                            borderRadius: 3,
                            p: 3,
                            textAlign: 'center',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                            border: '1px solid rgba(212, 175, 55, 0.1)',
                            animation: `${scaleIn} 0.5s ease ${0.2 + idx * 0.1}s forwards`,
                            opacity: 0,
                            height: '180px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            '&:hover': {
                              transform: 'translateY(-5px)',
                              boxShadow: '0 12px 30px rgba(212, 175, 55, 0.15)',
                              borderColor: alpha('#D4AF37', 0.3),
                            },
                          }}
                        >
                          <Box sx={{ color: '#D4AF37', mb: 1.5 }}>{stat.icon}</Box>
                          <Typography
                            variant="h4"
                            sx={{
                              color: '#0a0a2a',
                              fontWeight: 800,
                              fontSize: { xs: 28, md: 32 },
                              mb: 0.5,
                            }}
                          >
                            {stat.value}
                          </Typography>
                          <Typography sx={{ color: alpha('#0a0a2a', 0.7), fontWeight: 600, fontSize: '0.85rem' }}>
                            {stat.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Fade>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Section 3: Concierge Market Intelligence - Fixed width card */}
        <Box sx={{ py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="md">
            <UltraPremiumHeader 
              title="Concierge Market Intelligence" 
              subtitle="EXCLUSIVE INSIGHTS"
            />
            
            <Card
              elevation={0}
              sx={{
                bgcolor: '#FFFFFF',
                borderRadius: 3,
                p: { xs: 3, md: 4 },
                border: '1px solid rgba(212, 175, 55, 0.15)',
                animation: `${fadeInUp} 0.6s ease forwards`,
                opacity: 0,
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                minHeight: '500px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
                <AnalyticsIcon sx={{ color: '#D4AF37', fontSize: 24 }} />
                <Typography variant="h6" sx={{ color: '#0a0a2a', fontWeight: 700 }}>
                  Institutional Market Analysis
                </Typography>
                <Chip 
                  label="PRIVATE ACCESS" 
                  size="small"
                  sx={{ 
                    bgcolor: alpha('#D4AF37', 0.1), 
                    color: '#D4AF37',
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    height: 24,
                  }}
                />
              </Box>
              
              <Stack spacing={3}>
                {[
                  { 
                    title: "Gold Demand Surges Amid Global Uncertainty", 
                    date: "Jan 15, 2024", 
                    summary: "Central bank buying hits record highs as diversification accelerates across emerging markets.", 
                    impact: "Bullish",
                    prediction: "Target: $2,300"
                  },
                  { 
                    title: "Silver Industrial Applications Expand Rapidly", 
                    date: "Jan 12, 2024", 
                    summary: "Green technology adoption drives unprecedented demand for silver in solar panels and EVs.", 
                    impact: "Positive",
                    prediction: "Target: $28.50"
                  },
                  { 
                    title: "Platinum Group Metals Market Outlook", 
                    date: "Jan 10, 2024", 
                    summary: "Supply constraints and hydrogen economy create new opportunities for PGMs.", 
                    impact: "Neutral",
                    prediction: "Range: $880-$1,050"
                  },
                ].map((insight, idx) => (
                  <Box key={idx}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                      <Typography sx={{ color: '#0a0a2a', fontWeight: 700, fontSize: '1rem' }}>
                        {insight.title}
                      </Typography>
                      <Chip 
                        label={insight.impact}
                        size="small"
                        sx={{
                          bgcolor: insight.impact === 'Bullish' ? alpha('#4CAF50', 0.1) : 
                                   insight.impact === 'Positive' ? alpha('#2196F3', 0.1) : 
                                   alpha('#FF9800', 0.1),
                          color: insight.impact === 'Bullish' ? '#4CAF50' : 
                                 insight.impact === 'Positive' ? '#2196F3' : '#FF9800',
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          height: 22,
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                      <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>
                        {insight.date}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.7), lineHeight: 1.6, mb: 1 }}>
                      {insight.summary}
                    </Typography>
                    <Chip 
                      label={insight.prediction}
                      size="small"
                      sx={{
                        bgcolor: alpha('#D4AF37', 0.1),
                        color: '#D4AF37',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                      }}
                    />
                    {idx < 2 && <Divider sx={{ mt: 2, borderColor: alpha('#D4AF37', 0.1) }} />}
                  </Box>
                ))}
              </Stack>
              
              <Button
                fullWidth
                variant="outlined"
                endIcon={<ArrowForwardIcon />}
                sx={{ 
                  mt: 3, 
                  borderColor: '#D4AF37',
                  color: '#D4AF37',
                  py: 1,
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: '#D4AF37',
                    bgcolor: alpha('#D4AF37', 0.05),
                  },
                }}
              >
                ACCESS FULL BRIEFING
              </Button>
            </Card>
          </Container>
        </Box>

        {/* Section 4: Strategic Portfolio Allocation - consistent heights */}
        <Box sx={{ py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="lg">
            <UltraPremiumHeader 
              title="Strategic Portfolio Allocation" 
              subtitle="INDUSTRY EXPOSURE"
            />
            
            <Grid container spacing={2.5}>
              {[
                { industry: "Aerospace & Defense", percentage: 85, demand: "High", growth: "+12%" },
                { industry: "Electronics & Semiconductors", percentage: 92, demand: "Very High", growth: "+18%" },
                { industry: "Automotive & EV", percentage: 78, demand: "Growing", growth: "+25%" },
                { industry: "Medical Devices", percentage: 70, demand: "Stable", growth: "+8%" },
                { industry: "Investment & Banking", percentage: 88, demand: "Strong", growth: "+15%" },
                { industry: "Jewelry & Luxury", percentage: 75, demand: "Recovering", growth: "+10%" },
              ].map((item, idx) => (
                <Grid item xs={12} sm={6} key={idx}>
                  <Zoom in timeout={400 + idx * 80}>
                    <Card
                      elevation={0}
                      sx={{
                        bgcolor: '#FFFFFF',
                        borderRadius: 3,
                        p: 2.5,
                        transition: 'all 0.3s ease',
                        border: '1px solid rgba(212, 175, 55, 0.1)',
                        height: '140px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
                          borderColor: alpha('#D4AF37', 0.3),
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                        <Typography sx={{ color: '#0a0a2a', fontWeight: 700, fontSize: '1rem' }}>
                          {item.industry}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip 
                            label={item.demand}
                            size="small"
                            sx={{
                              bgcolor: item.demand === 'Very High' ? alpha('#4CAF50', 0.1) :
                                      item.demand === 'High' ? alpha('#2196F3', 0.1) :
                                      item.demand === 'Growing' ? alpha('#FF9800', 0.1) :
                                      alpha('#9E9E9E', 0.1),
                              color: item.demand === 'Very High' ? '#4CAF50' :
                                     item.demand === 'High' ? '#2196F3' :
                                     item.demand === 'Growing' ? '#FF9800' : '#9E9E9E',
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              height: 24,
                            }}
                          />
                          <Chip 
                            label={item.growth}
                            size="small"
                            icon={<TrendingUpIcon sx={{ fontSize: 12 }} />}
                            sx={{
                              bgcolor: alpha('#4CAF50', 0.1),
                              color: '#4CAF50',
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              height: 24,
                            }}
                          />
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography 
                          sx={{ 
                            color: '#D4AF37', 
                            fontWeight: 800, 
                            minWidth: 65, 
                            fontSize: { xs: 28, md: 32 },
                          }}
                        >
                          {item.percentage}%
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={item.percentage}
                          sx={{
                            flex: 1,
                            height: 6,
                            borderRadius: 3,
                            bgcolor: alpha('#D4AF37', 0.15),
                            '& .MuiLinearProgress-bar': {
                              background: 'linear-gradient(90deg, #D4AF37, #FFD700)',
                              borderRadius: 3,
                            }
                          }}
                        />
                      </Box>
                    </Card>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Section 5: Institutional Partners - consistent card heights */}
        <Box sx={{ py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="lg">
            <UltraPremiumHeader 
              title="Institutional Partners" 
              subtitle="GLOBAL RECOGNITION"
            />
            
            <Grid container spacing={2} justifyContent="center">
              {[
                { name: "LBMA", description: "Good Delivery List", tier: "Tier 1" },
                { name: "LPPM", description: "Platinum & Palladium", tier: "Accredited" },
                { name: "LME", description: "Registered Brand", tier: "Premium" },
                { name: "COMEX", description: "Approved Refiner", tier: "Tier 1" },
                { name: "DMCC", description: "Trade Partner", tier: "Strategic" },
                { name: "SBMA", description: "Bullion Member", tier: "Charter" },
              ].map((partner, idx) => (
                <Grid item xs={6} sm={4} md={2} key={idx}>
                  <Zoom in timeout={300 + idx * 50}>
                    <Card
                      elevation={0}
                      sx={{
                        bgcolor: '#FFFFFF',
                        borderRadius: 2.5,
                        p: 2.5,
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        border: '1px solid rgba(212, 175, 55, 0.1)',
                        cursor: 'pointer',
                        height: '180px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
                          borderColor: alpha('#D4AF37', 0.3),
                          '& .partner-icon': {
                            transform: 'scale(1.1)',
                            color: '#D4AF37',
                          },
                        },
                      }}
                    >
                      <GppGoodIcon 
                        className="partner-icon"
                        sx={{ 
                          fontSize: 36, 
                          color: '#0a0a2a', 
                          mb: 1,
                          transition: 'all 0.3s ease',
                        }} 
                      />
                      <Typography sx={{ color: '#0a0a2a', fontWeight: 700, fontSize: '1rem', mb: 0.25 }}>
                        {partner.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5), display: 'block' }}>
                        {partner.description}
                      </Typography>
                      <Chip 
                        label={partner.tier}
                        size="small"
                        sx={{
                          mt: 1,
                          bgcolor: alpha('#D4AF37', 0.1),
                          color: '#D4AF37',
                          fontSize: '0.55rem',
                          height: 18,
                          fontWeight: 600,
                        }}
                      />
                    </Card>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
            
            <Box sx={{ textAlign: 'center', mt: 5 }}>
              <Button
                variant="outlined"
                sx={{
                  borderColor: '#D4AF37',
                  color: '#D4AF37',
                  px: 4,
                  py: 1,
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: '#D4AF37',
                    bgcolor: alpha('#D4AF37', 0.05),
                  },
                }}
              >
                VIEW ALL CERTIFICATIONS
              </Button>
            </Box>
          </Container>
        </Box>

        <CTASection />
      </Box>
    </Box>
  );
};

export default Home;